

import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import LoadingSpinner from './components/common/LoadingSpinner';
import Layout from './components/layout/Layout';
import { SidebarProvider } from './context/SidebarContext';
import { AnioLectivoProvider } from './contexts/AnioLectivoContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './hooks/useAuth.jsx';
import { ParentPortalProvider } from './contexts/ParentPortalContext.jsx';
import ComprobantesList from './pages/ComprobantesList';

// ============================================================
// lazyWithRetry: wraps React.lazy() to handle chunk load failures.
// If the dynamic import fails (stale deployment), it retries once.
// If the retry also fails, it forces a page reload so the browser
// fetches the latest index.html with the correct chunk references.
// ============================================================
function lazyWithRetry(importFn) {
    return lazy(() =>
        importFn().catch((error) => {
            console.warn('[lazyWithRetry] Import failed, retrying in 1.5s...', error.message);
            return new Promise((resolve) => setTimeout(resolve, 1500))
                .then(() => importFn())
                .catch((retryError) => {
                    // Both attempts failed → force reload (only once to avoid loops)
                    const lastReload = sessionStorage.getItem('last_chunk_error_reload');
                    const now = Date.now();
                    if (!lastReload || now - parseInt(lastReload) > 10000) {
                        sessionStorage.setItem('last_chunk_error_reload', now.toString());
                        console.warn('[lazyWithRetry] Retry failed → reloading page...');
                        window.location.reload();
                    }
                    throw retryError;
                });
        })
    );
}

// Lazy Load Pages
const AdminCajaGlobal = lazyWithRetry(() => import('./pages/AdminCajaGlobal'));
const AuditoriaCajaUniformes = lazyWithRetry(() => import('./pages/AuditoriaCajaUniformes'));
const AgendaPsicologia = lazyWithRetry(() => import('./pages/AgendaPsicologia'));
const AniosLectivos = lazyWithRetry(() => import('./pages/AniosLectivos'));
const CentroAcademico = lazyWithRetry(() => import('./pages/CentroAcademico'));
const BackgroundRemover = lazyWithRetry(() => import('./pages/admin/BackgroundRemover'));
const AuditoriaCarnetsPersonal = lazyWithRetry(() => import('./pages/admin/AuditoriaCarnetsPersonal'));
const ApoderadoAsistencia = lazyWithRetry(() => import('./pages/ApoderadoAsistencia'));
const ApoderadoNotas = lazyWithRetry(() => import('./pages/ApoderadoNotas'));
const ApoderadoPagos = lazyWithRetry(() => import('./pages/ApoderadoPagos'));
const ApoderadoPsicologia = lazyWithRetry(() => import('./pages/ApoderadoPsicologia'));
const ApoderadoActitud = lazyWithRetry(() => import('./pages/ApoderadoActitud'));
const AreasAcademicas = lazyWithRetry(() => import('./pages/AreasAcademicas'));
const AsignacionCursos = lazyWithRetry(() => import('./pages/AsignacionCursos'));
const Asignaturas = lazyWithRetry(() => import('./pages/Asignaturas'));
const AsistenciaFacial = lazyWithRetry(() => import('./pages/AsistenciaFacial'));
const AsistenciaPuntoControl = lazyWithRetry(() => import('./pages/admin/AsistenciaPuntoControl'));
const Aulas = lazyWithRetry(() => import('./pages/Aulas'));
const CajaChica = lazyWithRetry(() => import('./pages/CajaChica'));
const CitasPsicologia = lazyWithRetry(() => import('./pages/CitasPsicologia'));
const ConfiguracionComprobantes = lazyWithRetry(() => import('./pages/ConfiguracionComprobantes'));
const ConfiguracionCuentas = lazyWithRetry(() => import('./pages/ConfiguracionCuentas'));
const ConfiguracionHorarios = lazyWithRetry(() => import('./pages/ConfiguracionHorarios'));
const ConfiguracionPagos = lazyWithRetry(() => import('./pages/ConfiguracionPagos'));
const CrearEvaluacion = lazyWithRetry(() => import('./pages/CrearEvaluacion'));
const CronogramasPagos = lazyWithRetry(() => import('./pages/CronogramasPagos'));
// Eager Load Critical Pages
const DetalleCurso = lazyWithRetry(() => import('./pages/DetalleCurso'));
const DirectorioEstudiantil = lazyWithRetry(() => import('./pages/DirectorioEstudiantil'));
const DisponibilidadAdmisiones = lazyWithRetry(() => import('./pages/DisponibilidadAdmisiones'));
const EstructuraAcademica = lazyWithRetry(() => import('./pages/EstructuraAcademica'));
const EvaluacionesPsicologia = lazyWithRetry(() => import('./pages/EvaluacionesPsicologia'));
const GeneradorBarcodes = lazyWithRetry(() => import('./pages/GeneradorBarcodes'));
const GestionAlumnos = lazyWithRetry(() => import('./pages/GestionAlumnos'));
const GestionCompetencias = lazyWithRetry(() => import('./pages/GestionCompetencias'));
const AulaVirtualDocente = lazyWithRetry(() => import('./pages/AulaVirtualDocente'));
const GestionTiposActividad = lazyWithRetry(() => import('./pages/GestionTiposActividad'));
const GestionUnidades = lazyWithRetry(() => import('./pages/GestionUnidades'));
const PortalAlumno = lazyWithRetry(() => import('./pages/PortalAlumno'));
// Eager Load Critical Pages
import GestionCaja from './pages/GestionCaja';
const GestionCursos = lazyWithRetry(() => import('./pages/GestionCursos'));
const GestionGastos = lazyWithRetry(() => import('./pages/GestionGastos'));
const Grados = lazyWithRetry(() => import('./pages/Grados'));
const LibretaNotas = lazyWithRetry(() => import('./pages/LibretaNotas'));
const ListaUsuarios = lazyWithRetry(() => import('./pages/ListaUsuarios'));
const ImportadorEspecialPersonal = lazyWithRetry(() => import('./pages/ImportadorEspecialPersonal'));
const LockScreen = lazyWithRetry(() => import('./pages/LockScreen'));
// Eager Load Critical Pages
import Login from './pages/Login';
const MallaCurricular = lazyWithRetry(() => import('./pages/MallaCurricular'));
const Matriculas = lazyWithRetry(() => import('./pages/Matriculas'));
const MovimientoAlumnos = lazyWithRetry(() => import('./pages/MovimientoAlumnos'));
const MatriculasRefactored = lazyWithRetry(() => import('./pages/MatriculasRefactored'));
const MiAsistencia = lazyWithRetry(() => import('./pages/MiAsistencia'));
const MiHorario = lazyWithRetry(() => import('./pages/MiHorario'));
const MisCalificaciones = lazyWithRetry(() => import('./pages/MisCalificaciones'));
const MisCursos = lazyWithRetry(() => import('./pages/MisCursos'));
const MisExamenes = lazyWithRetry(() => import('./pages/MisExamenes'));
const MobileCameraCapture = lazyWithRetry(() => import('./pages/MobileCameraCapture'));
const Niveles = lazyWithRetry(() => import('./pages/Niveles'));
const OpenDay = lazyWithRetry(() => import('./pages/OpenDay/OpenDay.jsx'));
const OpenDayAdmin = lazyWithRetry(() => import('./pages/OpenDay/OpenDayAdmin.jsx'));
const PersonalAsistencia = lazyWithRetry(() => import('./pages/personal/PersonalAsistencia'));
const PersonalHorario = lazyWithRetry(() => import('./pages/personal/PersonalHorario'));
const PersonalLogin = lazyWithRetry(() => import('./pages/PersonalLogin'));
const PlantillaBuilder = lazyWithRetry(() => import('./pages/PlantillaBuilder'));
const PlantillasList = lazyWithRetry(() => import('./pages/PlantillasList'));
const PlantillaViewer = lazyWithRetry(() => import('./pages/PlantillaViewer'));
const PlantillasDocumentos = lazyWithRetry(() => import('./pages/PlantillasDocumentos'));
const PublicAdmission = lazyWithRetry(() => import('./pages/PublicAdmission'));
const RealizarEvaluacion = lazyWithRetry(() => import('./pages/RealizarEvaluacion'));
const RecaudacionLibre = lazyWithRetry(() => import('./pages/RecaudacionLibre'));
const ReporteRecaudacion = lazyWithRetry(() => import('./pages/ReporteRecaudacion'));
const RegistroActitud = lazyWithRetry(() => import('./pages/RegistroActitud'));
const ReportesCajaChica = lazyWithRetry(() => import('./pages/ReportesCajaChica'));
const RolesPermissions = lazyWithRetry(() => import('./pages/RolesPermissions'));
const SabanaMinedu = lazyWithRetry(() => import('./pages/SabanaMinedu'));
const SabanaNotas = lazyWithRetry(() => import('./pages/SabanaNotas'));
const RellenarSiagie = lazyWithRetry(() => import('./pages/RellenarSiagie'));
const Sedes = lazyWithRetry(() => import('./pages/Sedes'));
const TestCameraCapture = lazyWithRetry(() => import('./pages/TestCameraCapture'));
const UsuariosEstudiantes = lazyWithRetry(() => import('./pages/UsuariosEstudiantes'));
const UsuariosEstudiantesRefactored = lazyWithRetry(() => import('./pages/UsuariosEstudiantesRefactored'));
const WhatsAppTest = lazyWithRetry(() => import('./pages/WhatsAppTest.jsx'));
const ReporteBalance = lazyWithRetry(() => import('./pages/contabilidad/ReporteBalance'));
const ReporteEgresos = lazyWithRetry(() => import('./pages/contabilidad/ReporteEgresos'));
const ReporteIngresos = lazyWithRetry(() => import('./pages/contabilidad/ReporteIngresos'));
const PanelSunat = lazyWithRetry(() => import('./pages/contabilidad/PanelSunat'));
const ReporteAsistenciaPersonal = lazyWithRetry(() => import('./pages/ReporteAsistenciaPersonal'));
const SystemGallery = lazyWithRetry(() => import('./pages/SystemGallery'));
const SesionRecursos = lazyWithRetry(() => import('./pages/shared/SesionRecursos'));
const CalendarioInstitucional = lazyWithRetry(() => import('./pages/shared/CalendarioInstitucional'));
const PuntoVentaUniformes = lazyWithRetry(() => import('./pages/PuntoVentaUniformes'));
const InventarioUniformes = lazyWithRetry(() => import('./pages/InventarioUniformes'));
const PedidosUniformes = lazyWithRetry(() => import('./pages/PedidosUniformes'));
const OrdenesCompraProveedores = lazyWithRetry(() => import('./pages/OrdenesCompraProveedores'));
const AuditoriaUniformes = lazyWithRetry(() => import('./pages/AuditoriaUniformes'));
const InventarioBienes = lazyWithRetry(() => import('./pages/InventarioBienes'));
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage'));
const RegularizadorApoderados = lazyWithRetry(() => import('./pages/RegularizadorApoderados'));

const AdminHorarios = lazyWithRetry(() => import('./pages/AdminHorarios'));
const GeneradorHorarios = lazyWithRetry(() => import('./pages/GeneradorHorarios'));

// Unit Detail Pages
const DetalleUnidadAlumno = lazyWithRetry(() => import('./pages/student/DetalleUnidadAlumno'));
const DetalleUnidadDocente = lazyWithRetry(() => import('./pages/teacher/DetalleUnidadDocente'));
const DetalleUnidadApoderado = lazyWithRetry(() => import('./pages/parent/DetalleUnidadApoderado'));
const DocenteMisCursos = lazyWithRetry(() => import('./pages/teacher/DocenteMisCursos'));
const InicioDocente = lazyWithRetry(() => import('./pages/teacher/InicioDocente'));
const PortalDocente = lazyWithRetry(() => import('./pages/teacher/PortalDocente'));
const DocenteMisAlumnos = lazyWithRetry(() => import('./pages/teacher/DocenteMisAlumnos'));
const DocenteActividadesSesion = lazyWithRetry(() => import('./pages/teacher/DocenteActividadesSesion'));
const DocenteRegistroAuxiliar = lazyWithRetry(() => import('./pages/teacher/DocenteRegistroAuxiliar'));
const DocenteImportarExcel = lazyWithRetry(() => import('./pages/teacher/DocenteImportarExcel'));
const DocenteImportarMasivo = lazyWithRetry(() => import('./pages/teacher/DocenteImportarMasivo'));
const DocenteDescargarPlantilla = lazyWithRetry(() => import('./pages/teacher/DocenteDescargarPlantilla'));
const EscanerNotas = lazyWithRetry(() => import('./pages/teacher/EscanerNotas'));
const ReportesAvanzados = lazyWithRetry(() => import('./pages/teacher/ReportesAvanzados'));
const GoogleSheetsHub = lazyWithRetry(() => import('./pages/GoogleSheetsHub'));
const ConfigurarEvaluacion = lazyWithRetry(() => import('./pages/teacher/ConfigurarEvaluacion'));
const AlumnoActividadesSesion = lazyWithRetry(() => import('./pages/student/AlumnoActividadesSesion'));
const GestionActividadesCurso = lazyWithRetry(() => import('./pages/teacher/GestionActividadesCurso'));
const HijosDatos = lazyWithRetry(() => import('./pages/parent/HijosDatos'));
const ApoderadoOrionEstudiantil = lazyWithRetry(() => import('./pages/parent/ApoderadoOrionEstudiantil'));
const GestionAccesos = lazyWithRetry(() => import('./pages/admin/GestionAccesos'));
const NormalizationPanel = lazyWithRetry(() => import('./pages/admin/NormalizationPanel'));
const GestionEvaluacionesAdmin = lazyWithRetry(() => import('./pages/admin/GestionEvaluacionesAdmin'));
const N8NDebugPage = lazyWithRetry(() => import('./pages/admin/N8NDebugPage'));
const AuditoriaCarnets = lazyWithRetry(() => import('./pages/admin/AuditoriaCarnets'));
const ReporteProyecto = lazyWithRetry(() => import('./pages/admin/ReporteProyecto'));
const LotesCarnets = lazyWithRetry(() => import('./pages/admin/LotesCarnets'));
const LoteProduccion = lazyWithRetry(() => import('./pages/admin/LoteProduccion'));
const LotesCarnetsPersonal = lazyWithRetry(() => import('./pages/admin/LotesCarnetsPersonal'));
const LoteProduccionPersonal = lazyWithRetry(() => import('./pages/admin/LoteProduccionPersonal'));
const JustificacionesAsistencia = lazyWithRetry(() => import('./pages/admin/JustificacionesAsistencia'));
const ReporteAsistenciaDetallado = lazyWithRetry(() => import('./pages/admin/ReporteAsistenciaDetallado'));
const GestionAsistenciaAula = lazyWithRetry(() => import('./pages/admin/GestionAsistenciaAula'));
const GestionHorariosAulas = lazyWithRetry(() => import('./pages/admin/GestionHorariosAulas'));




// Eager Load Critical Pages
import Inicio from './pages/Inicio';
const CarnetPreview = lazyWithRetry(() => import('./pages/CarnetPreview'));


const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return <div className="vh-100 d-flex justify-content-center align-items-center"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    if (!user) return <Navigate to="/login" />;
    return children;
};

// --- NEW HYBRID ROUTE PROTECTORS ---

const StaffRoute = ({ children }) => {
    const { user, loading, hasRole } = useAuth();
    if (loading) return <div className="vh-100 d-flex justify-content-center align-items-center"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>;
    if (!user) return <Navigate to="/login" />;

    if (hasRole('ADMIN') || hasRole('ADMINISTRADOR')) return children;

    // Check if user is PURELY a student or parent (no staff roles)
    const isOnlyStudent = user.roles?.every(r => {
        const name = typeof r === 'string' ? r : r.nombre;
        return name === 'ESTUDIANTE';
    });

    const isOnlyParent = user.roles?.every(r => {
        const name = typeof r === 'string' ? r : r.nombre;
        return name === 'APODERADO';
    });

    const isOnlyStudentOrParent = user.roles?.every(r => {
        const name = typeof r === 'string' ? r : r.nombre;
        return name === 'ESTUDIANTE' || name === 'APODERADO';
    });

    if (isOnlyStudent) return <Navigate to="/alumno/mis-cursos" replace />;
    if (isOnlyParent) return <Navigate to="/apoderado/asistencia" replace />; // Default apoderado home
    if (isOnlyStudentOrParent) return <Navigate to="/inicio" replace />;

    // If they have any other role (DOCENTE, CAJERO, SECRETARIA, etc.), let them in
    return children;
};

const StudentRoute = ({ children }) => {
    const { hasRole, hasPermission, loading, user } = useAuth();
    if (loading) return <LoadingSpinner />;
    if (!user) return <Navigate to="/login" />;

    // Si es ADMIN, lo dejamos pasar a cualquier lado
    if (hasRole('ADMIN') || hasRole('ADMINISTRADOR')) return children;

    // Si tiene el rol de ESTUDIANTE o el permiso de portal, lo dejamos pasar
    if (hasRole('ESTUDIANTE') || hasPermission('PORTAL_ESTUDIANTE_VER')) return children;

    // Si no es estudiante ni admin, lo sacamos
    return <Navigate to="/inicio" replace />;
};

const ParentRoute = ({ children }) => {
    const { hasRole, hasPermission, loading, user } = useAuth();
    if (loading) return <LoadingSpinner />;
    if (!user) return <Navigate to="/login" />;

    // Si es ADMIN, lo dejamos pasar
    if (hasRole('ADMIN') || hasRole('ADMINISTRADOR')) return children;

    // Si tiene el rol de APODERADO o el permiso del portal, lo dejamos pasar
    if (hasRole('APODERADO') || hasPermission('PORTAL_APODERADO_VER')) return children;

    return <Navigate to="/inicio" replace />;
};

const TeacherRoute = ({ children }) => {
    const { hasRole, hasPermission, loading, user } = useAuth();
    if (loading) return <LoadingSpinner />;
    if (!user) return <Navigate to="/login" />;

    // Si es ADMIN, lo dejamos pasar
    if (hasRole('ADMIN') || hasRole('ADMINISTRADOR')) return children;

    // Si tiene algún rol docente o permiso, lo dejamos pasar
    if (hasRole('DOCENTE') || hasRole('PROFESOR') || hasRole('AUXILIAR') || hasPermission('MODULO_ACADEMICO_DOCENTE_VER')) return children;

    // Si solo es estudiante, a sus cursos
    if (hasRole('ESTUDIANTE') && user.roles?.length === 1) return <Navigate to="/alumno/mis-cursos" replace />;

    return <Navigate to="/inicio" replace />;
};

const AdminRoute = ({ children }) => {
    const { hasRole, loading, user } = useAuth();
    if (loading) return <LoadingSpinner />;
    if (!user) return <Navigate to="/login" />;
    if (hasRole('ADMIN') || hasRole('ADMINISTRADOR')) return children;
    return <Navigate to="/inicio" replace />;
};

// Enchanced RequirePermission with automatic fallbacks
const RequirePermission = ({ permission, fallback, children }) => {
    const { hasPermission, loading, user, hasRole } = useAuth();
    if (loading) return <LoadingSpinner />;
    if (!user) return <Navigate to="/login" />;

    if (hasRole('ADMIN') || hasRole('ADMINISTRADOR')) return children;

    if (hasPermission(permission)) {
        return children;
    }

    // Si llegó hasta aquí, no tiene permiso.
    // Determinamos hacia dónde botarlo de forma segura:
    const isOnlyStudent = user.roles?.every(r => (typeof r === 'string' ? r : r.nombre) === 'ESTUDIANTE');
    const isOnlyParent = user.roles?.every(r => (typeof r === 'string' ? r : r.nombre) === 'APODERADO');

    if (isOnlyStudent) return <Navigate to="/alumno/mis-cursos" replace />;
    if (isOnlyParent) return <Navigate to="/apoderado/asistencia" replace />;

    return <Navigate to={fallback || "/inicio"} replace />;
};

const HomeRedirect = () => {
    const { hasRole } = useAuth();
    if (hasRole('ESTUDIANTE')) {
        return <Navigate to="/alumno/mis-cursos" replace />;
    }
    if (hasRole('DOCENTE') || hasRole('AUXILIAR') || hasRole('PROFESOR')) {
        return <Navigate to="/inicio" replace />;
    }
    return <Navigate to="/inicio" replace />;
};

const InicioComponent = () => {
    const { hasRole } = useAuth();
    if (hasRole('ESTUDIANTE')) {
        return <Navigate to="/alumno/mis-cursos" replace />;
    }
    // Si es ADMIN, mostramos el Inicio general (más potente)
    // Pero si es docente/auxiliar y NO es admin, mostramos el nuevo dashboard
    if (!hasRole('ADMIN') && !hasRole('ADMINISTRADOR')) {
        if (hasRole('DOCENTE') || hasRole('AUXILIAR') || hasRole('PROFESOR')) {
            return <InicioDocente />;
        }
    }
    return <Inicio />;
};

const ErrorPage = lazyWithRetry(() => import('./pages/ErrorPage'));

function App() {
    // PREFETCHING STRATEGY: Cargar "en secreto" las páginas secundarias más usadas
    // después de que la página principal haya cargado (3 segundos después)
    useEffect(() => {
        const prefetchSecondaryRoutes = async () => {
            try {
                // Lista de rutas comunes a pre-cargar
                const commonRoutes = [
                    import('./pages/CronogramasPagos'),
                    import('./pages/Matriculas'),
                    import('./pages/AsistenciaFacial'),
                    // ComprobantesList moved to eager load
                    import('./pages/contabilidad/ReporteIngresos'),
                    import('./pages/contabilidad/ReporteEgresos'),
                    import('./pages/CentroAcademico'),
                    import('./pages/ErrorPage')
                ];
                await Promise.all(commonRoutes);
                console.log('✨ Prefetching completed: Secondary routes loaded in background');
            } catch (error) {
                // Silent error (non-critical)
            }
        };

        const timer = setTimeout(() => {
            prefetchSecondaryRoutes();
        }, 3000); // Wait 3 seconds to prioritize initial render

        return () => clearTimeout(timer);
    }, []);

    return (
        <AnioLectivoProvider>
            <ThemeProvider>
                <SidebarProvider>
                    <AuthProvider>
                        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                            <Suspense fallback={<LoadingSpinner />}>
                                <Routes>
                                    <Route path="/open-day" element={<OpenDay />} />
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/personal/login" element={<PersonalLogin />} />
                                    <Route path="/asistencia" element={<AsistenciaFacial />} />
                                    <Route path="/lock-screen" element={<LockScreen />} />
                                    <Route path="/captura-foto/:sessionId" element={<MobileCameraCapture />} />
                                    <Route path="/admision" element={<PublicAdmission />} />
                                    <Route path="/carnet-preview" element={<CarnetPreview />} />


                                    {/* Error Routes */}
                                    <Route path="/error/400" element={<ErrorPage code={400} />} />
                                    <Route path="/error/403" element={<ErrorPage code={403} />} />
                                    <Route path="/error/404" element={<ErrorPage code={404} />} />
                                    <Route path="/error/500" element={<ErrorPage code={500} />} />
                                    <Route path="/error/503" element={<ErrorPage code={503} />} />

                                    <Route path="/" element={
                                        <ProtectedRoute>
                                            <Layout />
                                        </ProtectedRoute>
                                    }>
                                        <Route index element={<HomeRedirect />} />
                                        <Route path="inicio" element={<InicioComponent />} />
                                        <Route path="perfil" element={<ProfilePage />} />

                                        {/* =========================================
                                            STAFF ROUTES (Admin, Secretaria, Cajeros, etc)
                                            ========================================= */}
                                        <Route element={<StaffRoute><Outlet /></StaffRoute>}>
                                            <Route path="usuarios/lista" element={<ListaUsuarios />} />
                                            <Route path="usuarios/estudiantes" element={<ListaUsuarios />} />
                                            <Route path="usuarios/personal" element={<ListaUsuarios />} />
                                            <Route path="usuarios/personal/importar-especial" element={<ImportadorEspecialPersonal />} />
                                            <Route path="usuarios/apoderados" element={<ListaUsuarios />} />
                                            <Route path="usuarios/regularizar" element={<RegularizadorApoderados />} />
                                            <Route path="usuarios/gestion-accesos" element={<GestionAccesos />} />
                                            <Route path="usuarios/normalizacion" element={<NormalizationPanel />} />
                                            <Route path="admin/quitar-fondo" element={<BackgroundRemover />} />
                                            <Route path="admin/auditoria-carnets" element={<AuditoriaCarnets />} />
                                            <Route path="admin/auditoria-carnets-personal" element={<AuditoriaCarnetsPersonal />} />
                                            <Route path="admin/lotes-carnets-personal" element={<LotesCarnetsPersonal />} />
                                            <Route path="admin/lotes-carnets-personal/:id" element={<LoteProduccionPersonal />} />
                                            <Route path="admin/asistencia-control" element={<AsistenciaPuntoControl />} />
                                            <Route path="admin/asistencia-aula" element={<GestionAsistenciaAula />} />
                                            <Route path="admin/asistencia-reporte" element={<ReporteAsistenciaDetallado />} />
                                            <Route path="admin/justificaciones" element={<JustificacionesAsistencia />} />
                                            <Route path="admin/lotes-carnets" element={<LotesCarnets />} />
                                            <Route path="admin/lotes-carnets/:id" element={<LoteProduccion />} />
                                            <Route path="usuarios-estudiantes" element={<UsuariosEstudiantes />} />
                                            <Route path="/usuarios/nuevo-refactored" element={<UsuariosEstudiantesRefactored />} />
                                            <Route path="admin/n8n-debug" element={<N8NDebugPage />} />
                                            <Route path="admin/reporte-proyecto" element={<ReporteProyecto />} />
                                            <Route path="admin/gestion-evaluaciones" element={<RequirePermission permission="MODULO_ACADEMICO_ADMIN_VER"><GestionEvaluacionesAdmin /></RequirePermission>} />

                                            <Route path="academico/centro" element={<RequirePermission permission="CENTRO_ACADEMICO_VER"><CentroAcademico /></RequirePermission>} />
                                            <Route path="academico/estructura" element={<EstructuraAcademica />} />
                                            <Route path="sedes" element={<RequirePermission permission="SEDES_VER"><Sedes /></RequirePermission>} />
                                            <Route path="sedes/:id/comprobantes" element={<ConfiguracionComprobantes />} />
                                            <Route path="niveles" element={<RequirePermission permission="NIVELES_VER"><Niveles /></RequirePermission>} />
                                            <Route path="grados" element={<RequirePermission permission="GRADOS_VER"><Grados /></RequirePermission>} />
                                            <Route path="aulas" element={<RequirePermission permission="AULAS_VER"><Aulas /></RequirePermission>} />
                                            <Route path="academico/aulas-horarios" element={<RequirePermission permission="AULAS_VER"><GestionHorariosAulas /></RequirePermission>} />
                                            <Route path="anios-lectivos" element={<RequirePermission permission="ANIOS_LECTIVOS_VER"><AniosLectivos /></RequirePermission>} />
                                            <Route path="academico/unidades" element={<GestionUnidades />} />

                                            {/* Psychology Routes */}
                                            <Route path="psicologia/evaluaciones" element={<RequirePermission permission="PSICO_VER"><EvaluacionesPsicologia /></RequirePermission>} />
                                            <Route path="psicologia/crear" element={<RequirePermission permission="PSICO_VER"><CrearEvaluacion /></RequirePermission>} />
                                            <Route path="psicologia/editar/:id" element={<RequirePermission permission="PSICO_VER"><CrearEvaluacion /></RequirePermission>} />
                                            <Route path="psicologia/horario" element={<RequirePermission permission="PSICO_VER"><AgendaPsicologia /></RequirePermission>} />
                                            <Route path="psicologia/agenda" element={<RequirePermission permission="PSICO_VER"><AgendaPsicologia /></RequirePermission>} />
                                            <Route path="psicologia/citas" element={<RequirePermission permission="PSICO_VER"><CitasPsicologia /></RequirePermission>} />
                                            <Route path="psicologia/directorio" element={<RequirePermission permission="PSICO_VER"><DirectorioEstudiantil /></RequirePermission>} />
                                            <Route path="psicologia/disponibilidad-admisiones" element={<RequirePermission permission="PSICO_VER"><DisponibilidadAdmisiones /></RequirePermission>} />
                                            <Route path="psicologia/plantillas" element={<RequirePermission permission="PSICO_VER"><PlantillasList /></RequirePermission>} />
                                            <Route path="psicologia/plantillas/nueva" element={<RequirePermission permission="PSICO_VER"><PlantillaBuilder /></RequirePermission>} />
                                            <Route path="psicologia/plantillas/:id/editar" element={<RequirePermission permission="PSICO_VER"><PlantillaBuilder /></RequirePermission>} />
                                            <Route path="psicologia/plantillas/:id/ver" element={<RequirePermission permission="PSICO_VER"><PlantillaViewer /></RequirePermission>} />
                                            <Route path="psicologia/evaluaciones/:evaluacionId/realizar" element={<RequirePermission permission="PSICO_VER"><RealizarEvaluacion /></RequirePermission>} />

                                            {/* Módulo de Documentos y Contratos */}
                                            <Route path="documentos/plantillas" element={<RequirePermission permission="DOCUMENTOS_CONTRATOS_VER"><PlantillasDocumentos /></RequirePermission>} />

                                            {/* Enrollment Management */}
                                            <Route path="matriculas" element={<RequirePermission permission="MATRICULA_VER"><Matriculas /></RequirePermission>} />
                                            <Route path="matriculas/gestionar/:id" element={<RequirePermission permission="MATRICULA_VER"><MatriculasRefactored /></RequirePermission>} />
                                            <Route path="matriculas/nueva" element={<RequirePermission permission="MATRICULA_VER"><MatriculasRefactored /></RequirePermission>} />
                                            <Route path="configuracion-pagos" element={<RequirePermission permission="PAGOS_CONFIG_VER"><ConfiguracionPagos /></RequirePermission>} />

                                            {/* Caja / Payments */}
                                            <Route path="caja/gestion" element={<RequirePermission permission="CAJA_GESTION_VER"><GestionCaja /></RequirePermission>} />
                                            <Route path="caja/chica" element={<RequirePermission permission="CAJA_CHICA_VER"><CajaChica modulo="CHICA" /></RequirePermission>} />
                                            <Route path="caja/verano" element={<RequirePermission permission="CAJA_VERANO_VER"><CajaChica modulo="VERANO" /></RequirePermission>} />
                                            <Route path="caja/academia" element={<RequirePermission permission="CAJA_ACADEMIA_VER"><CajaChica modulo="ACADEMIA" /></RequirePermission>} />
                                            <Route path="caja/oficina" element={<RequirePermission permission="CAJA_OFICINA_VER"><CajaChica modulo="OFICINA" /></RequirePermission>} />
                                            <Route path="caja/admin" element={<RequirePermission permission="CAJA_ADMIN_VER"><ReportesCajaChica /></RequirePermission>} />
                                            <Route path="caja/administracion" element={<RequirePermission permission="CAJA_GLOBAL_VER"><AdminCajaGlobal /></RequirePermission>} />
                                            <Route path="caja/gastos" element={<RequirePermission permission="CAJA_EGRESOS_VER"><GestionGastos /></RequirePermission>} />
                                            <Route path="caja/cuentas" element={<RequirePermission permission="CAJA_CONFIG_VER"><ConfiguracionCuentas /></RequirePermission>} />
                                            <Route path="caja/recaudacion" element={<RequirePermission permission="RECAUDACION_REPORTE_VER"><ReporteRecaudacion /></RequirePermission>} />
                                            <Route path="caja/recaudacion-libre" element={<RequirePermission permission="CAJA_RECAUDACION_LIBRE_VER"><RecaudacionLibre /></RequirePermission>} />
                                            <Route path="caja/cronogramas" element={<RequirePermission permission="CRONOGRAMAS_VER"><CronogramasPagos /></RequirePermission>} />
                                            <Route path="comprobantes" element={<RequirePermission permission="COMPROBANTES_VER"><ComprobantesList /></RequirePermission>} />

                                            {/* Uniformes Module */}
                                            <Route path="uniformes/venta" element={<RequirePermission permission="UNIFORMES_VENTA_VER"><PuntoVentaUniformes /></RequirePermission>} />
                                            <Route path="uniformes/inventario" element={<RequirePermission permission="UNIFORMES_INV_VER"><InventarioUniformes /></RequirePermission>} />
                                            <Route path="uniformes/pedidos" element={<RequirePermission permission="UNIFORMES_PEDIDOS_VER"><PedidosUniformes /></RequirePermission>} />
                                            <Route path="uniformes/compras" element={<RequirePermission permission="UNIFORMES_INV_VER"><OrdenesCompraProveedores /></RequirePermission>} />
                                            <Route path="uniformes/auditoria" element={<RequirePermission permission="UNIFORMES_INV_VER"><AuditoriaUniformes /></RequirePermission>} />
                                            <Route path="caja/uniformes" element={<RequirePermission permission="UNIFORMES_CAJA_VER"><CajaChica modulo="UNIFORMES" /></RequirePermission>} />
                                            <Route path="uniformes/auditoria-caja" element={<RequirePermission permission="UNIFORMES_CAJA_VER"><AuditoriaCajaUniformes /></RequirePermission>} />

                                            {/* Inventario de Bienes Patrimoniales */}
                                            <Route path="inventario/bienes" element={<RequirePermission permission="INVENTARIO_BIENES_VER"><InventarioBienes /></RequirePermission>} />

                                            {/* Contabilidad */}
                                            <Route path="contabilidad/ingresos" element={<RequirePermission permission="CONTAB_INGRESOS_VER"><ReporteIngresos /></RequirePermission>} />
                                            <Route path="contabilidad/egresos" element={<RequirePermission permission="CONTAB_EGRESOS_VER"><ReporteEgresos /></RequirePermission>} />
                                            <Route path="contabilidad/balance" element={<RequirePermission permission="CONTAB_BALANCE_VER"><ReporteBalance /></RequirePermission>} />
                                            <Route path="contabilidad/sunat" element={<RequirePermission permission="CONTAB_BALANCE_VER"><PanelSunat /></RequirePermission>} />

                                            {/* Security */}
                                            <Route path="seguridad/roles" element={<RequirePermission permission="ROLES_VER"><RolesPermissions /></RequirePermission>} />
                                            <Route path="configuracion/horarios" element={<RequirePermission permission="RRHH_HORARIOS_VER"><ConfiguracionHorarios /></RequirePermission>} />

                                            {/* Módulo Académico Admin */}
                                            <Route path="academico/areas" element={<RequirePermission permission="AREAS_VER"><AreasAcademicas /></RequirePermission>} />
                                            <Route path="academico/asignaturas" element={<RequirePermission permission="ASIGNATURAS_VER"><Asignaturas /></RequirePermission>} />
                                            <Route path="academico/malla-curricular" element={<RequirePermission permission="MALLA_VER"><MallaCurricular /></RequirePermission>} />
                                            <Route path="academico/cursos" element={<RequirePermission permission="CURSOS_VER"><GestionCursos /></RequirePermission>} />
                                            <Route path="academico/asignar-cursos" element={<RequirePermission permission="MODULO_ACADEMICO_ADMIN_VER"><AsignacionCursos /></RequirePermission>} />
                                            <Route path="academico/alumnos" element={<RequirePermission permission="ALUMNOS_VER"><GestionAlumnos /></RequirePermission>} />
                                            <Route path="academico/competencias" element={<RequirePermission permission="COMPETENCIAS_MINEDU_VER"><GestionCompetencias /></RequirePermission>} />
                                            <Route path="academico/tipos-actividad" element={<RequirePermission permission="ESTRUCTURA_ACADEMICA_VER"><GestionTiposActividad /></RequirePermission>} />
                                            <Route path="academico/movimiento-alumnos" element={<RequirePermission permission="ALUMNOS_VER"><MovimientoAlumnos /></RequirePermission>} />
                                            <Route path="academico/actitud" element={<RegistroActitud />} />
                                            <Route path="academico/google-sheets" element={<GoogleSheetsHub />} />
                                            <Route path="academico/sabana-minedu" element={<RequirePermission permission="SABANA_NOTAS_VER"><SabanaMinedu /></RequirePermission>} />

                                            <Route path="academico/calendario" element={<CalendarioInstitucional />} />

                                            <Route path="academico/sabana-notas" element={<RequirePermission permission="SABANA_NOTAS_VER"><SabanaNotas /></RequirePermission>} />
                                            <Route path="academico/rellenar-siagie" element={<RequirePermission permission="SABANA_NOTAS_VER"><RellenarSiagie /></RequirePermission>} />
                                            <Route path="academico/libreta-notas" element={<RequirePermission permission="LIBRETA_NOTAS_VER"><LibretaNotas /></RequirePermission>} />
                                            <Route path="academico/barcodes" element={<RequirePermission permission="BARCODES_VER"><GeneradorBarcodes /></RequirePermission>} />

                                            <Route path="admin/open-day" element={<OpenDayAdmin />} />
                                            <Route path="admin/galeria" element={<SystemGallery />} />

                                            {/* Rutas de Personal */}
                                            <Route path="personal/horario" element={<PersonalHorario />} />
                                            <Route path="personal/asistencia" element={<PersonalAsistencia />} />
                                            <Route path="personal/reporte" element={<AdminRoute><ReporteAsistenciaPersonal /></AdminRoute>} />

                                            {/* Horarios Académicos */}
                                            <Route path="academico/horarios" element={<AdminHorarios />} />
                                            <Route path="academico/generador-horarios" element={<GeneradorHorarios />} />
                                        </Route>

                                        {/* =========================================
                                            TEACHER ROUTES (Docentes)
                                            ========================================= */}
                                        <Route element={<TeacherRoute><Outlet /></TeacherRoute>}>
                                            <Route path="docente/mis-cursos" element={<DocenteMisCursos />} />
                                            <Route path="docente/curso/:id" element={<PortalDocente />} />
                                            <Route path="docente/mis-alumnos" element={<DocenteMisAlumnos />} />
                                            <Route path="docente/unidad/:id" element={<DetalleUnidadDocente />} />
                                            <Route path="docente/sesion/:id/actividades" element={<DocenteActividadesSesion />} />
                                            {/* Reportes Avanzados */}
                                            <Route path="docente/reportes-avanzados" element={<ReportesAvanzados />} />

                                            <Route path="docente/curso/:id/registro-auxiliar" element={<DocenteRegistroAuxiliar />} />
                                            <Route path="docente/curso/:id/importar-excel" element={<DocenteImportarExcel />} />
                                            <Route path="docente/importar-masivo" element={<DocenteImportarMasivo />} />
                                            <Route path="docente/descargar-plantilla" element={<DocenteDescargarPlantilla />} />
                                            <Route path="docente/rellenar-siagie" element={<RellenarSiagie />} />
                                            <Route path="docente/google-sheets" element={<GoogleSheetsHub />} />
                                            <Route path="docente/curso/:id/escaner-notas" element={<EscanerNotas />} />
                                            <Route path="docente/curso/:id/configurar-evaluacion" element={<ConfigurarEvaluacion />} />
                                            <Route path="docente/curso/:id/actividades" element={<GestionActividadesCurso />} />
                                            <Route path="academico/aula-docente" element={<AulaVirtualDocente />} />
                                            {/* Mi Horario para Docente */}
                                            <Route path="docente/mi-horario" element={<MiHorario />} />
                                        </Route>

                                        {/* =========================================
                                            STUDENT ROUTES (Alumnos)
                                            ========================================= */}
                                        <Route element={<StudentRoute><Outlet /></StudentRoute>}>
                                            <Route path="alumno/mis-cursos" element={<MisCursos />} />
                                            <Route path="alumno/curso-detalle" element={<DetalleCurso />} />
                                            <Route path="alumno/mis-calificaciones" element={<MisCalificaciones />} />
                                            <Route path="alumno/mis-examenes" element={<MisExamenes />} />
                                            <Route path="alumno/mi-asistencia" element={<MiAsistencia />} />
                                            <Route path="alumno/mi-horario" element={<MiHorario />} />
                                            <Route path="alumno/unidad/:id" element={<DetalleUnidadAlumno />} />
                                            <Route path="alumno/sesion/:sesionId/tareas" element={<AlumnoActividadesSesion />} />
                                            <Route path="academico/mi-aula" element={<PortalAlumno alumnoId={null} />} />
                                        </Route>

                                        {/* =========================================
                                            PARENT ROUTES (Apoderados)
                                            ========================================= */}
                                        <Route element={<ParentRoute><ParentPortalProvider><Outlet /></ParentPortalProvider></ParentRoute>}>
                                            <Route path="apoderado/asistencia" element={<ApoderadoAsistencia />} />
                                            <Route path="apoderado/notas" element={<ApoderadoNotas />} />
                                            <Route path="apoderado/psicologia" element={<ApoderadoPsicologia />} />
                                            <Route path="apoderado/actitud" element={<ApoderadoActitud />} />
                                            <Route path="apoderado/pagos" element={<ApoderadoPagos />} />
                                            <Route path="apoderado/datos-hijos" element={<HijosDatos />} />
                                            <Route path="apoderado/unidad/:id" element={<DetalleUnidadApoderado />} />
                                            <Route path="apoderado/orion-estudiantil" element={<ApoderadoOrionEstudiantil />} />
                                        </Route>

                                        {/* Shared / Utility Routes */}
                                        <Route path="academico/recursos/sesion/:id" element={<SesionRecursos />} />
                                        <Route path="academico/recursos/unidad/:id" element={<SesionRecursos />} />
                                        <Route path="/test-camera" element={<TestCameraCapture />} />
                                        <Route path="/asistencia-facial" element={<AsistenciaFacial />} />
                                        <Route path="whatsapp-test" element={<WhatsAppTest />} />
                                    </Route>

                                    {/* Catch-all route for 404 */}
                                    <Route path="*" element={<ErrorPage code={404} />} />
                                </Routes>
                            </Suspense>
                        </BrowserRouter>
                    </AuthProvider>
                </SidebarProvider>
            </ThemeProvider>
        </AnioLectivoProvider>
    );
}

export default App;

